import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getKidSub } from '@/auth/authStore';
import { useWsEvent } from '@/lib/useWsEvent';

import {
  analysisInProgress,
  getStudyAnalysis,
  getStudyDocumentAnalysis,
  STUDY_ANALYSIS_EVENT,
  STUDY_ANALYSIS_POLL_MS,
  STUDY_ANALYSIS_QUERY_KEY,
  STUDY_DOCUMENT_ANALYSIS_QUERY_KEY,
  type StudyAnalysis,
  type StudyAnalysisQuestionStatus,
  type StudyAnalysisRefundState,
  type StudyAnalysisStatus,
} from './studyAnalysisApi';
import { analysisRefundPending, errorCode } from './studyAnalysisErrors';

export interface StudyAnalysisEventPayload {
  analysis_id: string;
  status: StudyAnalysisStatus;
  /** Sent when an owed refund was settled later: `{ status: 'failed', refund_state: 'refunded' }`. */
  refund_state?: StudyAnalysisRefundState;
}

/**
 * Slow fallback while a refund is owed. The late settlement normally arrives as
 * `study.analysis.updated`; this only matters when the WebSocket is down.
 */
export const STUDY_ANALYSIS_REFUND_POLL_MS = 60_000;

/** A "not found" answer means "no analysis yet", not an error to show. */
function notFound(error: unknown): boolean {
  const code = errorCode(error);
  return code === 'STUDY_ANALYSIS_NOT_FOUND' || code === 'HTTP_404';
}

/**
 * The newest analysis of a document, or null when it has none. Study Home reads the same query
 * key for its Next step, so it shares the request with the material progress line.
 */
export async function readDocumentAnalysis(documentId: string): Promise<StudyAnalysis | null> {
  try {
    return (await getStudyDocumentAnalysis(documentId)) ?? null;
  } catch (error) {
    if (notFound(error)) return null;
    throw error;
  }
}

/**
 * One analysis, kept current by the `study.analysis.updated` event with a 3-second poll as the
 * fallback while it is queued or running (PRD §3.1). Pass an analysis id, or a document id for
 * the newest analysis covering that document.
 */
export function studyAnalysisQueryKey(target: {
  analysisId?: string | null;
  documentId?: string | null;
}) {
  const kidId = getKidSub();
  return target.analysisId
    ? [...STUDY_ANALYSIS_QUERY_KEY(target.analysisId), kidId]
    : [...STUDY_DOCUMENT_ANALYSIS_QUERY_KEY(target.documentId ?? ''), kidId];
}

/** Reads one analysis; for a document target, "no analysis yet" is null, not an error. */
export async function loadStudyAnalysis(target: {
  analysisId?: string | null;
  documentId?: string | null;
}): Promise<StudyAnalysis | null> {
  const byId = Boolean(target.analysisId);
  try {
    const analysis = byId
      ? await getStudyAnalysis(target.analysisId!)
      : await getStudyDocumentAnalysis(target.documentId!);
    return analysis ?? null;
  } catch (error) {
    if (!byId && notFound(error)) return null;
    throw error;
  }
}

export function useStudyAnalysis(target: {
  analysisId?: string | null;
  documentId?: string | null;
}) {
  const queryClient = useQueryClient();
  const byId = Boolean(target.analysisId);
  const queryKey = studyAnalysisQueryKey(target);
  const query = useQuery({
    queryKey,
    queryFn: () => loadStudyAnalysis(target),
    enabled: byId || Boolean(target.documentId),
    retry: false,
    refetchInterval: (current) => {
      const analysis = current.state.data ?? null;
      if (analysisInProgress(analysis)) return STUDY_ANALYSIS_POLL_MS;
      return analysis && analysisRefundPending(analysis) ? STUDY_ANALYSIS_REFUND_POLL_MS : false;
    },
  });
  useWsEvent<StudyAnalysisEventPayload>(
    STUDY_ANALYSIS_EVENT,
    (payload) => {
      if (!payload) return;
      // By id, only this analysis matters. By document, any event may be a newer analysis of it.
      if (byId && payload.analysis_id !== target.analysisId) return;
      void queryClient.invalidateQueries({ queryKey });
    },
    [queryKey.join('|')],
  );
  return query;
}

export type QuestionState = StudyAnalysisQuestionStatus;

/**
 * Question progress as the server counts it (PRD §4.3): each question's status comes from the
 * Study Sessions linked to it, and the totals count only the questions shown now.
 */
export function questionProgress(analysis: StudyAnalysis | null | undefined) {
  const states = new Map(
    (analysis?.result?.questions ?? []).map((question) => [question.id, question.status]),
  );
  return {
    stateOf: (questionId: string): QuestionState => states.get(questionId) ?? 'not_started',
    done: analysis?.progress.questions_done ?? 0,
    total: analysis?.progress.questions_total ?? 0,
  };
}
